import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet } from '../lib/api-client';
import { formatMoney } from '../lib/money';
import { applyTableQuery, columnParam, columnsMatch, nextSort, pageCount, pageSlice, readPageSize, writePageSize, type ColumnSort } from '../lib/list-query';
import { useLedgerReload } from '../lib/use-ledger';
import { Alert, Tabs } from '../components/ui/Badge';
import { BarChart } from '../components/ui/BarChart';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { ExcelButton, PageHead, PageSizeSelect, SortHeader } from '../components/ui/chrome';
import { Input, Select } from '../components/ui/Input';
import { customerChoices, useFreshRows } from '../lib/select-choices';
import { enumLabel } from '../lib/labels';
import { createInvoiceActions, invoiceCreateInstruction } from '../lib/invoice-create';
import { PayablePanel, PositionPanel, type PayableReport, type PositionReport } from './reports/FinancePanels';
import { ResourcingReport } from './reports/Resourcing';
import { RecordAssistDialog } from '../components/records/RecordAssistDialog';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../components/ui/ColumnSearch';

const KINDS = ['support', 'hosting', 'services', 'licence', 'other'];
const HOME_COLUMNS = [
  { key: 'customer', label: 'Customer', text: (row: HomeRow) => row.customerName },
  { key: 'line', label: 'Charge line', text: (row: HomeRow) => row.name },
  { key: 'po', label: 'PO', text: (row: HomeRow) => row.poNumber },
  { key: 'model', label: 'Model', text: (row: HomeRow) => `${enumLabel(row.chargeModel)} / ${enumLabel(row.kind)}` },
  { key: 'ready', label: 'Ready', text: (row: HomeRow) => formatMoney(row.amountPence), sort: (row: HomeRow) => row.amountPence },
  { key: 'hours', label: 'Hours', text: (row: HomeRow) => row.hours ?? '', sort: (row: HomeRow) => row.hours ?? '' },
  { key: 'blocker', label: 'Blocker', text: (row: HomeRow) => row.blocker },
  { key: 'remaining', label: 'PO remaining', text: (row: HomeRow) => formatMoney(row.poRemainingPence), sort: (row: HomeRow) => row.poRemainingPence },
  { key: 'days', label: 'PO days', text: (row: HomeRow) => row.daysToExpiry ?? '', sort: (row: HomeRow) => row.daysToExpiry ?? '' },
];

interface HomeRow {
  chargeLineId: string;
  customerName: string;
  name: string;
  poNumber: string;
  poId?: string;
  chargeModel: string;
  kind: string;
  amountPence: number;
  hours?: number;
  blocker: string;
  poRemainingPence: number;
  daysToExpiry: number | null;
  draftInvoiceId?: string;
  currency?: string;
  lines?: { description: string; qty: number; unitPence: number }[];
}

interface HomeData {
  periodName?: string;
  yearStart?: string;
  yearEnd?: string;
  invoicedYearPence?: number;
  invoicedNetYearPence?: number;
  unpaidInvoicesPence?: number;
  uninvoicedPence?: number;
  uninvoicedWorkPence?: number;
  draftsPence?: number;
  drafts?: unknown[];
  rows?: HomeRow[];
  wipPeriod?: { start?: string; end?: string };
  wipPeriodName?: string;
  period?: { start?: string; end?: string };
  unmappedClockify?: { error?: string; people?: unknown[]; projects?: unknown[] };
  cash?: { overduePence: number; thisMonthPence: number; nextMonthPence: number; laterPence: number; undatedPence: number };
  revenue?: { spread: Record<string, number>; gross?: Record<string, number>; committedPence: number; quotedPence: number };
}

export function HomePage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [createRow, setCreateRow] = useState<HomeRow | null>(null);
  const [choiceRow, setChoiceRow] = useState<HomeRow | null>(null);
  const [params, setParams] = useSearchParams();
  const report = params.get('report') || 'receivable';
  const [customerId, setCustomerId] = useState(params.get('customerId') || '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ColumnSort | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => readPageSize('finance-home'));
  const readySearch = useColumnSearch();
  const cashSearch = useColumnSearch();

  const homeQuery = useQuery({
    queryKey: ['finance-home', customerId, from, to],
    queryFn: () => {
      const query = new URLSearchParams();
      if (customerId) query.set('customerId', customerId);
      if (from) query.set('from', from);
      if (to) query.set('to', to);
      const suffix = query.toString();
      return apiGet<HomeData>(`/api/finance/home${suffix ? `?${suffix}` : ''}`);
    },
  });
  const payableQuery = useQuery({
    queryKey: ['finance-payable', customerId, from, to],
    enabled: report === 'payable',
    queryFn: () => {
      const query = new URLSearchParams();
      if (customerId) query.set('customerId', customerId);
      if (from) query.set('from', from);
      if (to) query.set('to', to);
      const suffix = query.toString();
      return apiGet<PayableReport>(`/api/finance/payable${suffix ? `?${suffix}` : ''}`);
    },
  });
  const positionQuery = useQuery({
    queryKey: ['finance-position', customerId, from, to],
    enabled: report === 'position',
    queryFn: () => {
      const query = new URLSearchParams();
      if (customerId) query.set('customerId', customerId);
      if (from) query.set('from', from);
      if (to) query.set('to', to);
      const suffix = query.toString();
      return apiGet<PositionReport>(`/api/finance/position${suffix ? `?${suffix}` : ''}`);
    },
  });
  const [customers, reloadCustomers] = useFreshRows<{ id: string; code?: string; name: string; description?: string }>('/api/reporting-customers');
  const home = homeQuery.data;
  const fromValue = from || home?.yearStart || '';
  const toValue = to || home?.yearEnd || '';
  const revenue = home?.revenue;

  function reload() {
    void queryClient.invalidateQueries({ queryKey: ['finance-home'] });
    void queryClient.invalidateQueries({ queryKey: ['finance-payable'] });
    void queryClient.invalidateQueries({ queryKey: ['finance-position'] });
  }
  function setReport(id: string) {
    const next = new URLSearchParams(params);
    if (id === 'receivable') next.delete('report');
    else next.set('report', id);
    setParams(next, { replace: true });
  }
  const wipHint = home?.wipPeriodName ? `${home.wipPeriodName}, plus draft invoices` : 'Last month, plus draft invoices';
  const choice = choiceRow ? createInvoiceActions(choiceRow) : null;
  useLedgerReload('home', reload);

  const readyKey = columnParam(readySearch.applied);
  useEffect(() => { setPage(1); }, [readyKey]);
  const filtered = useMemo(() => applyTableQuery(home?.rows || [], search, HOME_COLUMNS, sort).filter((row) => columnsMatch(Object.fromEntries(HOME_COLUMNS.map((column) => [column.key, String(column.text(row) ?? '')])), readySearch.applied)), [home, search, sort, readySearch.applied]);
  const pages = pageCount(filtered.length, pageSize);
  const paged = pageSlice(filtered, Math.min(page, pages), pageSize);
  const unmapped = (home?.unmappedClockify?.people?.length || 0) + (home?.unmappedClockify?.projects?.length || 0);
  function invoiceList(kind?: string) {
    const params = new URLSearchParams({ filter: 'invoiced', sort: 'issuedOn', dir: 'desc' });
    if (fromValue) params.set('from', fromValue);
    if (toValue) params.set('to', toValue);
    if (customerId) params.set('reportingCustomerId', customerId);
    if (kind) params.set('kind', kind);
    return `/invoices?${params}`;
  }

  return (
    <div className="space-y-4">
      <PageHead crumbs={[{ label: 'Overview' }, { label: 'Dashboard' }]} title="Dashboard" subtitle="Your financial position and next actions." />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs prominent tabs={[{ id: 'receivable', label: 'Receivable' }, { id: 'payable', label: 'Payable' }, { id: 'position', label: 'Position' }, { id: 'resourcing', label: 'Resourcing' }]} value={report} onChange={setReport} />
        <Link className="text-sm text-[#895CF6] underline" to="/reports/layouts">Saved layouts</Link>
      </div>
      {report === 'resourcing' ? null : (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs">Customer
            <Select searchable className="mt-1 h-8" choices={customerChoices(customers, 'All customers')} onOpen={reloadCustomers} value={customerId} onChange={(event) => setCustomerId(event.target.value)} />
          </label>
          <label className="text-xs">From
            <Input className="mt-1 h-8" type="date" value={fromValue} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <label className="text-xs">To
            <Input className="mt-1 h-8" type="date" value={toValue} onChange={(event) => setTo(event.target.value)} />
          </label>
          <span className="pb-2 text-sm text-gray-500">{home?.periodName}</span>
        </div>
      )}
      {report === 'receivable' ? <>
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Invoiced by kind</h2>
        {revenue ? (
          <div className="grid gap-4 md:grid-cols-5">
            {KINDS.map((kind) => (
              <Link key={kind} to={invoiceList(kind)} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="text-xs text-gray-500">{enumLabel(kind)}</div>
                <div className="mt-2 flex flex-wrap items-baseline gap-x-3">
                  <div className="text-2xl">{formatMoney(revenue.spread[kind])}</div>
                  <div className="text-sm tabular-nums text-gray-500">With VAT {formatMoney(revenue.gross?.[kind])}</div>
                </div>
              </Link>
            ))}
          </div>
        ) : null}
      </section>
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Receivable totals</h2>
        <div className="grid gap-4 md:grid-cols-4">
        {([
          ['Unpaid invoices', formatMoney(home?.unpaidInvoicesPence), '/invoices?filter=unpaid', '', ''],
          ['Invoiced', formatMoney(home?.invoicedNetYearPence), invoiceList(), '', typeof home?.invoicedYearPence === 'number' ? `With VAT ${formatMoney(home.invoicedYearPence)}` : ''],
          ['Committed', formatMoney(revenue?.committedPence), '/purchase-orders?filter=unspent', 'Left on purchase orders', ''],
          ['Quoted', formatMoney(revenue?.quotedPence), '/quotations?filter=issued', 'Issued quotations with no linked order', ''],
          ['Uninvoiced work', formatMoney(home?.uninvoicedWorkPence), '', wipHint, ''],
          ['Draft invoices', formatMoney(home?.draftsPence), '/invoices?filter=drafts', '', ''],
          ['Awaiting expected date', formatMoney(home?.cash?.undatedPence), '', '', ''],
        ] as const).map(([label, value, to, hint, aside]) => {
          const body = (
            <>
              <div className="text-xs uppercase tracking-wide text-[var(--bo-faint)]">{label}</div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-3">
                <div className="text-2xl font-semibold tabular-nums">{value}</div>
                {aside ? <div className="text-sm tabular-nums text-[var(--bo-muted)]">{aside}</div> : null}
              </div>
              {hint ? <div className="mt-1 text-xs text-[var(--bo-muted)]">{hint}</div> : null}
            </>
          );
          const className = 'rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4';
          return to ? <Link key={label} to={to} className={className}>{body}</Link> : <div key={label} className={className}>{body}</div>;
        })}
        </div>
      </section>
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
          <h2 className="mb-3 text-sm font-semibold">Invoiced by kind</h2>
          <BarChart rows={KINDS.map((kind) => ({ label: enumLabel(kind), value: revenue?.spread?.[kind] || 0 }))} />
        </div>
        <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
          <h2 className="mb-3 text-sm font-semibold">Quoted, committed, and invoiced</h2>
          <BarChart rows={[{ label: 'Quoted', value: revenue?.quotedPence || 0 }, { label: 'Committed', value: revenue?.committedPence || 0 }, { label: 'Invoiced', value: home?.invoicedNetYearPence || 0 }]} />
        </div>
      </section>
      <h2 className="text-lg font-semibold">Needs attention</h2>
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <div className="flex items-center justify-between gap-4 rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
          <div>
            <div className="font-medium">{unmapped} unmapped time entries</div>
            <div className="text-sm text-[var(--bo-muted)]">Assign Clockify entries to charge lines.</div>
          </div>
          <Link className="rounded-lg bg-[#895CF6] px-3 py-2 text-sm text-white" to="/charge-lines">Review entries</Link>
        </div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
          <div>
            <div className="font-medium">{formatMoney(home?.cash?.undatedPence)} without an expected date</div>
            <div className="text-sm text-[var(--bo-muted)]">Add dates to improve the collection forecast.</div>
          </div>
          <Link className="rounded-lg bg-[#895CF6] px-3 py-2 text-sm text-white" to="/invoices">Review dates</Link>
        </div>
      </div>
      <div className="mb-6 grid gap-4 lg:grid-cols-5">
        <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4 lg:col-span-3">
          <h2 className="font-semibold">Expected receipts</h2>
          <p className="mb-3 text-sm text-[var(--bo-muted)]">Amounts we expect to receive, based on invoice expected dates.</p>
          <table className="bo-cards w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[var(--bo-faint)]">
                <th className="py-2 font-medium">When</th>
                <th className="py-2 text-right font-medium">Amount</th>
                <th className="w-8 py-2 text-right"><ColumnFilterToggle open={cashSearch.open} onClick={cashSearch.toggle} /></th>
              </tr>
              {cashSearch.open ? <ColumnFilterInputs columns={[{ key: 'when', label: 'When' }, { key: 'amount', label: 'Amount' }]} draft={cashSearch.draft} onChange={cashSearch.set} trailing /> : null}
            </thead>
            <tbody>
              {([
                ['Overdue', home?.cash?.overduePence],
                ['This month', home?.cash?.thisMonthPence],
                ['Next month', home?.cash?.nextMonthPence],
                ['Later', home?.cash?.laterPence],
                ['No expected date', home?.cash?.undatedPence],
              ] as const).filter(([label, value]) => cashSearch.matches({ when: label, amount: formatMoney(value) })).map(([label, value]) => (
                <tr key={label} className="border-t border-[var(--bo-border)]">
                  <td className="py-2" data-label="When">{label}</td>
                  <td className="py-2 text-right tabular-nums" data-label="Amount">{formatMoney(value)}</td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4 lg:col-span-2">
          <h2 className="font-semibold">Ready to invoice</h2>
          <p className="text-sm text-[var(--bo-muted)]">Uninvoiced work from the last calendar month.</p>
        </div>
      </div>
      {home?.unmappedClockify?.error ? <Alert error>Clockify: {home.unmappedClockify.error}</Alert> : null}
      <Input className="h-8 max-w-xs" placeholder="Search charge lines" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
      <div className="mb-3 flex items-center justify-between text-sm">
        <span className="flex items-center gap-3"><span>{filtered.length} lines · page {Math.min(page, pages)} of {pages}</span><PageSizeSelect value={pageSize} onChange={(size) => { setPageSize(size); writePageSize('finance-home', size); setPage(1); }} /><ExcelButton filename="Ready to invoice" headers={HOME_COLUMNS.map((column) => column.label)} rows={filtered.map((row) => [row.customerName, row.name, row.poNumber || '', `${enumLabel(row.chargeModel)} / ${enumLabel(row.kind)}`, row.amountPence / 100, row.hours ?? '', row.blocker, row.poRemainingPence / 100, row.daysToExpiry ?? ''])} /></span>
        <div className="flex gap-2">
          <Button size="xs" variant="outline" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>Prev</Button>
          <Button size="xs" variant="outline" disabled={page >= pages} onClick={() => setPage((n) => n + 1)}>Next</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <table className="bo-cards min-w-full text-left text-sm">
          <thead><tr className="border-b border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400">{HOME_COLUMNS.map((column) => <SortHeader key={column.key} className="px-3 py-2 font-medium" label={column.label} active={sort?.key === column.key} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, column.key)); setPage(1); }} />)}<th className="px-3 py-2 text-right font-medium"><span className="mr-2">Actions</span><ColumnFilterToggle open={readySearch.open} onClick={readySearch.toggle} /></th></tr>
          {readySearch.open ? <ColumnFilterInputs columns={HOME_COLUMNS.map((column) => ({ key: column.key, label: column.label }))} draft={readySearch.draft} onChange={readySearch.set} trailing /> : null}
          </thead>
          <tbody>
            {paged.map((row) => (
              <tr key={row.chargeLineId} className={row.blocker === 'po_expired' || (row.daysToExpiry !== null && row.daysToExpiry <= 30) ? 'text-red-600' : ''}>
                <td className="border-b border-gray-100 px-3 py-3 dark:border-gray-800" data-label="Customer">{row.customerName}</td>
                <td className="border-b border-gray-100 px-3 py-3 dark:border-gray-800" data-label="Charge line">{row.name}</td>
                <td className="border-b border-gray-100 px-3 py-3 dark:border-gray-800" data-label="PO">{row.poNumber}</td>
                <td className="border-b border-gray-100 px-3 py-3 dark:border-gray-800" data-label="Model">{enumLabel(row.chargeModel)} / {enumLabel(row.kind)}</td>
                <td className="border-b border-gray-100 px-3 py-3 dark:border-gray-800" data-label="Ready">{formatMoney(row.amountPence)}</td>
                <td className="border-b border-gray-100 px-3 py-3 dark:border-gray-800" data-label="Hours">{row.hours ?? ''}</td>
                <td className="border-b border-gray-100 px-3 py-3 dark:border-gray-800" data-label="Blocker">{row.blocker}</td>
                <td className="border-b border-gray-100 px-3 py-3 dark:border-gray-800" data-label="PO remaining">{formatMoney(row.poRemainingPence)}</td>
                <td className="border-b border-gray-100 px-3 py-3 dark:border-gray-800" data-label="PO days">{row.daysToExpiry}</td>
                <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3 text-right dark:border-gray-800" data-label="">
                  {row.draftInvoiceId ? (
                    <Link className="text-xs font-medium text-[#895CF6] underline" to={`/invoices/${row.draftInvoiceId}`}>View draft</Link>
                  ) : (
                    <button type="button" className="text-xs font-medium text-[#895CF6] underline" onClick={() => setChoiceRow(row)}>Create invoice</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!filtered.length ? <p className="px-4 py-8 text-sm text-[var(--bo-muted)]">No uninvoiced work found for the last calendar month.</p> : null}
      </div>
      </> : null}
      {report === 'payable' ? (payableQuery.isError ? <Alert error>Could not load payable.</Alert> : payableQuery.data ? <PayablePanel data={payableQuery.data} /> : <p className="text-sm text-[var(--bo-muted)]">Loading</p>) : null}
      {report === 'position' ? (positionQuery.isError ? <Alert error>Could not load the position.</Alert> : positionQuery.data ? <PositionPanel data={positionQuery.data} /> : <p className="text-sm text-[var(--bo-muted)]">Loading</p>) : null}
      {report === 'resourcing' ? <ResourcingReport embedded /> : null}
      {choice && choiceRow ? (
        <Dialog title="Create invoice" onClose={() => setChoiceRow(null)} footer={
          <>
            <Button onClick={() => { setCreateRow(choiceRow); setChoiceRow(null); }}>Create with AI</Button>
            {choice.poPath ? <Button variant="outline" onClick={() => navigate(choice.poPath!)}>Raise from PO</Button> : null}
          </>
        }>
          <p className="text-sm">Create with AI writes this row into the assistant. Raise from PO opens the order and its create-invoice dialog.</p>
        </Dialog>
      ) : null}
      {createRow ? (
        <RecordAssistDialog
          key={createRow.chargeLineId}
          kind="invoice"
          mode="create"
          title="Invoice"
          instruction={invoiceCreateInstruction(createRow, home?.wipPeriod)}
          onClose={() => setCreateRow(null)}
          onManual={() => navigate(`/invoices/new?chargeLineId=${encodeURIComponent(createRow.chargeLineId)}`)}
          onSaved={(saved) => {
            const next = String(saved.id || '');
            if (next) navigate(`/invoices/${next}`);
          }}
        />
      ) : null}
    </div>
  );
}
